import React, { useState, useEffect } from 'react';
import { 
    MdPeople, 
    MdAssignment, 
    MdTrendingUp, 
    MdStar,
    MdCalendarToday,
    MdMoreVert
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import API_BASE_URL from '../../../config/api';

const HODDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [reportingSummary, setReportingSummary] = useState({ reported_goals: 0, not_reported: 0 });
    const baseUrl = API_BASE_URL;

    // Fetch profile first to get department_id
    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                });
                setProfile(response.data);
            } catch (err) {
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Fetch analytics for the department once profile is loaded
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.department) return;
            setLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { department: profile.employee.department }
                });
                setAnalytics(response.data);
            } catch (err) {
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        if (profile && !profileLoading) {
            fetchAnalytics();
        }
    }, [profile, profileLoading]);

    // Fetch reporting summary after analytics loads
    useEffect(() => {
        const fetchReportingSummary = async () => {
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/reporting/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                setReportingSummary(response.data);
            } catch (err) {
                setReportingSummary({ reported_goals: 0, not_reported: 0 });
            }
        };
        if (analytics) fetchReportingSummary();
    }, [analytics]);

    const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];
    const stats = analytics?.overall_statistics || {};
    const monthlyPerformance = analytics?.monthly_performance || [];

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = React.useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [analytics]);

    // Prepare data for reporting summary pie chart
    const reportingPieData = [
        { name: 'Reported', value: reportingSummary.reported_goals },
        { name: 'Not Reported', value: reportingSummary.not_reported }
    ];
    const reportingPieColors = ['#4F46E5', '#ef4444'];

    // Custom label for pie chart to avoid cut-off
    const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 18;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={13} fontWeight={600}>
                {`${name}: ${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    const StatCard = ({ icon: Icon, title, value, trend, loading }) => (
        <div className="stat-card">
            {loading ? (
                <Skeleton height={100} />
            ) : (
                <>
                    <div className="stat-icon">
                        <Icon />
                    </div>
                    <div className="stat-content">
                        <h3>{title}</h3>
                        <div className="stat-value">{value}</div>
                        {trend && <div className="stat-trend">{trend}</div>}
                    </div>
                </>
            )}
        </div>
    );

    const ChartCard = ({ title, children, loading }) => (
        <div className="chart-card">
            {loading ? (
                <Skeleton height={300} />
            ) : (
                <>
                    <div className="chart-header">
                        <h3>{title}</h3>
                        <button className="more-btn">
                            <MdMoreVert />
                        </button>
                    </div>
                    <div className="chart-content">
                        {children}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <>
        <Topbar />
        <Sidebar />
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Performance Dashboard</h1>
                <p>Overview of employee performance metrics and KPIs</p>
            </div>

            <div className="stats-grid">
                <StatCard
                    icon={MdPeople}
                    title="Total Employees"
                    value={stats.total_employees}
                    loading={loading}
                />
                
                <StatCard
                    icon={MdTrendingUp}
                    title="Average Performance"
                    value={stats.average_progress ? `${stats.average_progress}%` : '0%'}
                    trend={stats.average_progress_trend}
                    loading={loading}
                />
                <StatCard
                    icon={MdTrendingUp}
                    title="Reporting Rate"
                    value={
                        reportingSummary.reporting_rate !== undefined && reportingSummary.reporting_rate !== null
                            ? `${Number(reportingSummary.reporting_rate).toFixed(2)}%`
                            : '0.00%'
                    }
                    trend={stats.reviews_completed_trend}
                    loading={loading}
                />
                <StatCard
                    icon={MdStar}
                    title="Completed KPIs"
                    value={`${stats.completed_goals} / ${stats.total_goals}`}
                    loading={loading}
                />
                
            </div>

            <div className="charts-grid">
            
                <ChartCard title="Quarterly Performance Trends" loading={loading}>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="quarter" />
                            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Area 
                                type="monotone" 
                                dataKey="completion_rate" 
                                stroke="#4F46E5" 
                                fillOpacity={1} 
                                fill="url(#colorQuarterly)" 
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Reporting Summary" loading={loading}>
                    <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                            <Pie
                                data={reportingPieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                                labelLine={true}
                                label={renderPieLabel}
                            >
                                {reportingPieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={reportingPieColors[index % reportingPieColors.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
        </>
    );
};

export default HODDashboard;
